import { fetchAuth } from "../../../shared/api/ApiService";
import {
    ICancelledLessonItem,
    IDebugRecalculateWeeklyPlanSnapshotResponse,
    IGetStatisticsRequest,
    IGetStatisticsResponse,
    IListCancelledLessonsResponse,
    IListRegularScheduleLossesResponse,
    IRegularScheduleLossItem,
    IStatisticsConversion,
    IStatisticsMetric,
    IStatisticsSummary,
    TStatisticsFaultParty
} from "../model/types";

type TAnyRecord = Record<string, any>;

const emptyMetric = (label: string): IStatisticsMetric => ({
    label,
    hours: '0',
    count: '0',
})

const emptyConversion = (): IStatisticsConversion => ({
    plannedTrials: '0',
    completedTrials: '0',
    createdStudents: '0',
    rate: '0',
    completedRate: '0',
    lostBeforeTrial: '0',
    completedConversionBase: '0',
})

const mapMetric = (metric: TAnyRecord | undefined, fallbackLabel: string): IStatisticsMetric => ({
    label: String(metric?.label ?? fallbackLabel),
    hours: String(metric?.hours ?? '0'),
    count: String(metric?.count ?? '0'),
})

const mapConversion = (conversion: TAnyRecord | undefined): IStatisticsConversion => ({
    plannedTrials: String(conversion?.plannedTrials ?? conversion?.planned_trials ?? '0'),
    completedTrials: String(conversion?.completedTrials ?? conversion?.completed_trials ?? '0'),
    createdStudents: String(conversion?.createdStudents ?? conversion?.created_students ?? '0'),
    rate: String(conversion?.rate ?? '0'),
    completedRate: String(conversion?.completedRate ?? conversion?.completed_rate ?? '0'),
    lostBeforeTrial: String(conversion?.lostBeforeTrial ?? conversion?.lost_before_trial ?? '0'),
    completedConversionBase: String(conversion?.completedConversionBase ?? conversion?.completed_conversion_base ?? '0'),
})

const mapRegularScheduleLossItem = (item: TAnyRecord): IRegularScheduleLossItem => ({
    studentId: String(item?.studentId ?? item?.student_id ?? '0'),
    studentName: String(item?.studentName ?? item?.student_name ?? ''),
    oldWeeklyHours: String(item?.oldWeeklyHours ?? item?.old_weekly_hours ?? '0'),
    newWeeklyHours: String(item?.newWeeklyHours ?? item?.new_weekly_hours ?? '0'),
    lostHours: String(item?.lostHours ?? item?.lost_hours ?? '0'),
    changedAt: String(item?.changedAt ?? item?.changed_at ?? ''),
    changeType: String(item?.changeType ?? item?.change_type ?? ''),
})

const normalizeFaultParty = (value: unknown): TStatisticsFaultParty => (
    value === 'tutor' ? 'tutor' : 'student'
)

const mapCancelledLessonItem = (item: TAnyRecord): ICancelledLessonItem => ({
    eventId: String(item?.eventId ?? item?.event_id ?? '0'),
    lessonId: String(item?.lessonId ?? item?.lesson_id ?? '0'),
    studentId: String(item?.studentId ?? item?.student_id ?? '0'),
    studentName: String(item?.studentName ?? item?.student_name ?? ''),
    tutorUserId: String(item?.tutorUserId ?? item?.tutor_user_id ?? '0'),
    tutorName: String(item?.tutorName ?? item?.tutor_name ?? ''),
    tgAccountId: String(item?.tgAccountId ?? item?.tg_account_id ?? '0'),
    tgAccountName: String(item?.tgAccountName ?? item?.tg_account_name ?? ''),
    tagId: String(item?.tagId ?? item?.tag_id ?? '0'),
    tagName: String(item?.tagName ?? item?.tag_name ?? ''),
    startsAt: String(item?.startsAt ?? item?.starts_at ?? ''),
    hours: String(item?.hours ?? '0'),
    faultParty: normalizeFaultParty(item?.faultParty ?? item?.fault_party),
    cancelledAt: String(item?.cancelledAt ?? item?.cancelled_at ?? ''),
    comment: String(item?.comment ?? ''),
})

export const mapRegularSchedulePlan = (plan?: TAnyRecord): IStatisticsMetric =>
    mapMetric(plan, 'План по постоянному графику')

const mapSummary = (summary: TAnyRecord = {}): IStatisticsSummary => ({
    planned: {...mapMetric(summary.planned, 'Запланировано'), label: 'Запланировано'},
    regularSchedulePlan: mapRegularSchedulePlan(summary.regularSchedulePlan ?? summary.regular_schedule_plan),
    completed: mapMetric(summary.completed, 'Проведено'),
    completedRescheduled: mapMetric(summary.completedRescheduled ?? summary.completed_rescheduled, 'Проведено перенесённых занятий'),
    extraLessons: mapMetric(summary.extraLessons ?? summary.extra_lessons, 'Дополнительные занятия'),
    studentCancelled: mapMetric(summary.studentCancelled ?? summary.student_cancelled, 'Отмены по вине студентов'),
    tutorCancelled: mapMetric(summary.tutorCancelled ?? summary.tutor_cancelled, 'Отмены по вине исполнителей'),
    studentRescheduled: mapMetric(summary.studentRescheduled ?? summary.student_rescheduled, 'Переносы по вине студентов'),
    tutorRescheduled: mapMetric(summary.tutorRescheduled ?? summary.tutor_rescheduled, 'Переносы по вине исполнителей'),
    unknownEvents: mapMetric(summary.unknownEvents ?? summary.unknown_events, 'Неясная причина'),
    makeups: mapMetric(summary.makeups, 'Отработки'),
    lostHours: mapMetric(summary.lostHours ?? summary.lost_hours, 'Потеря часов'),
    regularScheduleLostHours: mapMetric(
        summary.regularScheduleLostHours ?? summary.regular_schedule_lost_hours,
        'Сливы постоянного графика'
    ),
    gainedHours: mapMetric(summary.gainedHours ?? summary.gained_hours, 'Прибавка часов'),
    netHours: String(summary.netHours ?? summary.net_hours ?? '0'),
    conversion: mapConversion(summary.conversion),
})

export const EMPTY_STATISTICS_SUMMARY: IStatisticsSummary = {
    planned: emptyMetric('Запланировано'),
    regularSchedulePlan: mapRegularSchedulePlan(),
    completed: emptyMetric('Проведено'),
    completedRescheduled: emptyMetric('Проведено перенесённых занятий'),
    extraLessons: emptyMetric('Дополнительные занятия'),
    studentCancelled: emptyMetric('Отмены по вине студентов'),
    tutorCancelled: emptyMetric('Отмены по вине исполнителей'),
    studentRescheduled: emptyMetric('Переносы по вине студентов'),
    tutorRescheduled: emptyMetric('Переносы по вине исполнителей'),
    unknownEvents: emptyMetric('Неясная причина'),
    makeups: emptyMetric('Отработки'),
    lostHours: emptyMetric('Потеря часов'),
    regularScheduleLostHours: emptyMetric('Сливы постоянного графика'),
    gainedHours: emptyMetric('Прибавка часов'),
    netHours: '0',
    conversion: emptyConversion(),
}

class StatisticsService {
    async getStatistics(data: IGetStatisticsRequest): Promise<IGetStatisticsResponse> {
        const res = await fetchAuth(`${process.env.REACT_APP_SERVER_URL_ADMIN}/api/v1/statistics`, {
            method: 'POST',
            body: JSON.stringify(data),
        })
        const response = await res.json()

        return {
            viewer: response.viewer,
            summary: mapSummary(response.summary),
        }
    }

    async listRegularScheduleLosses(data: IGetStatisticsRequest): Promise<IListRegularScheduleLossesResponse> {
        const res = await fetchAuth(`${process.env.REACT_APP_SERVER_URL_ADMIN}/api/v1/statistics/regular-schedule-losses`, {
            method: 'POST',
            body: JSON.stringify(data),
        })
        const response = await res.json()

        return {
            viewer: response.viewer,
            items: Array.isArray(response.items) ? response.items.map(mapRegularScheduleLossItem) : [],
        }
    }

    async listCancelledLessons(data: IGetStatisticsRequest, faultParty: TStatisticsFaultParty): Promise<IListCancelledLessonsResponse> {
        const res = await fetchAuth(`${process.env.REACT_APP_SERVER_URL_ADMIN}/api/v1/statistics/cancelled-lessons`, {
            method: 'POST',
            body: JSON.stringify({
                ...data,
                faultParty,
            }),
        })
        const response = await res.json()

        return {
            viewer: response.viewer,
            items: Array.isArray(response.items) ? response.items.map(mapCancelledLessonItem) : [],
        }
    }

    async debugRecalculateWeeklyPlanSnapshot(): Promise<IDebugRecalculateWeeklyPlanSnapshotResponse> {
        const res = await fetchAuth(`${process.env.REACT_APP_SERVER_URL_ADMIN}/api/v1/statistics/debug/recalculate_weekly_plan_snapshot`, {
            method: 'POST',
            body: JSON.stringify({}),
        })
        const response = await res.json()

        return {
            viewer: response.viewer,
            weekStartsAt: String(response.weekStartsAt ?? response.week_starts_at ?? ''),
        }
    }
}

export const statisticsService = new StatisticsService()
